<template lang="pug">
	.easy-creative.full-width
		Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions') + '..'", :fullscreen="true")
		.flex-col.align-center(v-show="!isLoading")
			.flex-col.full-width.header-panel-holder.align-center.center(ref="header")
				.flex-row.align-center.center.space.header-panel.full-width
					.flex-row.align-center.space-medium
						.font-big.upper(v-if="branding && branding.CompanyName") {{branding.CompanyName}}
						img.company-logo(v-if="companyLogoUrl", :src="companyLogoUrl")
					.flex-col.space.center.powered-by
						.font-tiny.upper.grey {{$tCaption('Powered by')}}
						img.adthos-logo(:src="require('assets/logo_transparent.png')")
				template(v-if="mode === 'questions' && page !== pages[0] && displayingSteps && displayingSteps.length > 1")
					.flex-row.align-center.center.space.header-stepper.full-width
						.flex-col
							.flex-row.align-center.center.space.header-stepper
								.step.flex-row.space.align-center(v-for="(step, idx) in displayingSteps", v-show="step.allowBack === void 0 || step.allowBack()", :class="{ active: idx === activeStepIdx, passed: idx <= stepper.maxPassed }", @click="(idx <= stepper.maxPassed) && onDisplayStepClick(step, idx)")
									fas.grey(v-if="idx > 0", icon="right-long")
									.step-name {{step.name}}
							.stepper-line
								.stepper-value(:style="{ '--percent': `${ Math.min(100, 100 * (stepper.maxPassed + 1) / displayingSteps.length) }%` }")
				template(v-else)
					.flex-row.align-center.center.space.header-stepper.full-width
				.top-right-panel
					.flex-row.space-large.align-center
						.header-button.clickable.hoverable.flex-row.align-center.center.space(v-if="page === 'questions' && isDebug", @click="showGenerateAnswers")
							fas(icon="wand-magic-sparkles")
							span {{$tCaption('Generate answers')}}
						.header-button.clickable.hoverable.flex-row.align-center.center.space(v-if="expertModeEnabled", @click="gotoExpertMode")
							fas(icon="sliders")
							span {{$tCaption('Expert mode')}}
						.header-button.clickable.hoverable.flex-row.align-center.center.space(v-if="sourceBriefId", @click="gotoSimpleMode")
							fas(icon="wand-magic-sparkles")
							span {{$tCaption('Simple mode')}}
						.header-button.clickable.hoverable.flex-row.align-center.center.space(@click="$refs.feedback.show()")
							fas(icon="message")
							span {{$tCaption('Leave feedback')}}
			DxScrollView.easy-creative-main-scroll-view(ref="scrollView", width="100%", showScrollbar="always", :style="{ '--top': `${headerOffsetHeight}px` }")
				.flex-col.full-width.full-height.align-center.center
					template(v-if="$auth.hasCreativeApi() || $auth.easyCreativeOnly()")
						EasyCreativeStartPage.page(ref="start", v-if="pageActivated('start')", v-show="page === 'start'", @back="back", @page="goToPage", :info="info")
						EasyCreativeQuestions.page(ref="questions", v-if="pageActivated('questions')", v-show="page === 'questions'", @back="back", @page="goToPage", :info="info",
							@steps="$forceUpdate()", @active-step-changed="$forceUpdate(); refreshScrollView(true);", @next="hasAiScriptGenerator() ? goToPage('scriptBuilder', true) : goToPage('results', true)")
						EasyCreativeScriptBuilder.page(ref="scriptBuilder", v-if="pageActivated('scriptBuilder')", v-show="page === 'scriptBuilder'", @back="back", @page="goToPage", :info="info", @next="scriptBuilderNext")
						EasyCreativeVoiceAdjectives.page(ref="voiceSelector", v-show="page === 'voiceSelector'", @back="back", @page="goToPage", :info="info", @next="goToPage('adjectives', true)")
						EasyCreativeAdjectives.page(ref="adjectives", v-if="pageActivated('adjectives')", v-show="page === 'adjectives'", @back="back", @page="goToPage", :info="info", @next="onMusicAdjectivesNext")
						EasyCreativeMusicSelection.page(ref="musicSelection", v-if="pageActivated('musicSelection')", v-show="page === 'musicSelection'", @back="back", @page="goToPage", :info="info", @next="onMusicSelectionNext" )
						EasyCreativeAudioGenerator.page(ref="audioGenerator", v-if="pageActivated('audioGenerator')", v-show="page === 'audioGenerator'", @back="back", @page="goToPage", :info="info", @next="goToPage('results', true)")
							EasyCreativeFastChange(:info="info", slot="fast-change")
						EasyCreativeTemplates.page(ref="templates", v-if="pageActivated('templates')", v-show="page === 'templates'", @back="back", @page="goToPage", :info="info", @next="goToPage('results', true)")
						EasyCreativeResults.page(ref="results", v-show="page === 'results'", @back="back", @page="goToPage", :info="info")
					template(v-else)
						.font-big.tb-padding {{$tCaption('Creative Studio subscription has expired.')}}
		template(v-if="isEditor")
			.adt-button.rounded.editor-button.clickable.hoverable.padded-lg.flex-row.align-center.center(v-if="isEditor", @click="$refs.settings.show()")
				fas(icon="cog")
			EasyCreativeSettings(ref="settings", :info="info")
		EasyCreativeFeedback(ref="feedback", :info="info")
		EasyCreativeProducerRequest(ref="producerRequest", :info="info", @page="goToPage")
		EasyCreativeGenerateAnswers(ref="generateAnswers", :info="info")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import EasyCreativeStartPage from './EasyCreativeStartPage.vue';
	import EasyCreativeTemplates from './EasyCreativeTemplates.vue';
	import EasyCreativeQuestions from './EasyCreativeQuestions.vue';
	import EasyCreativeScriptBuilder from './EasyCreativeScriptBuilder.vue';
	import EasyCreativeResults from './EasyCreativeResults.vue';
	import EasyCreativeVoiceAdjectives from './EasyCreativeVoiceAdjectives.vue';
	import EasyCreativeAdjectives from './EasyCreativeAdjectives.vue';
	import EasyCreativeAudioGenerator from './EasyCreativeAudioGenerator.vue';
	import EasyCreativeSettings from './EasyCreativeSettings.vue';
	import EasyCreativeMusicSelection from './EasyCreativeMusicSelection.vue';
	import EasyCreativeFeedback from './EasyCreativeFeedback.vue';
	import EasyCreativeProducerRequest from './EasyCreativeProducerRequest.vue';
	import EasyCreativeFastChange from './EasyCreativeFastChange.vue';
	import EasyCreativeGenerateAnswers from './EasyCreativeGenerateAnswers.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import resizeObserver from 'resize-observer';
	import brandingsMixin from './brandings.js';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative',
		props: ['apiKey', 'briefId', 'editor', 'integrationInfo', 'predefined', 'sourceBriefId'],
		mixins: [brandingsMixin, cognitiveArrays],
		components: {
			Loader, DxButton, EasyCreativeStartPage, EasyCreativeTemplates, EasyCreativeQuestions, EasyCreativeResults, EasyCreativeScriptBuilder, EasyCreativeVoiceAdjectives,
			EasyCreativeAdjectives, EasyCreativeAudioGenerator, EasyCreativeSettings, EasyCreativeMusicSelection, DxScrollView, EasyCreativeFeedback, EasyCreativeProducerRequest,
			EasyCreativeFastChange, EasyCreativeGenerateAnswers
		},
		data() {
			const pages = ['start', 'questions', 'templates', 'scriptBuilder', 'adjectives', 'musicSelection', 'voiceSelector', 'audioGenerator',  'results'];
			return {
				isLoading: false,
				pages,
				page: pages[0],
				activatedPages: { start: true },
				mode: !this.fixedMode ? null : (this.fixedMode === 'builder' ? 'questions' : 'templates'),
				brief: null,
				branding: null,
				lastBriefAnswers: null,
				headerOffsetHeight: 0,
				stepper: {
					mainPages: [
						//{ id: 'start', text: this.$tCaption('Start'), },
						{ id: 'questions', text: this.$tCaption('Brief') },
						{ id: 'scriptBuilder', text: this.$tCaption('Script writer'), allowBack: () => !this.info.skippedSteps.script }, 
						{ id: 'adjectives', text: this.$tCaption('Music adjectives'), visible: () => this.getMusicAdjectivesVisible(), allowBack: () => !this.info.skippedSteps.music },
						{ id: 'musicSelection', text: this.$tCaption('Music selection'), visible: () => this.getMusicSelectionIsVisible(), allowBack: () => !this.info.skippedSteps.music }, 
						{ id: 'voiceSelector', text: this.$tCaption('Voice selection'), visible: () => this.$refs.scriptBuilder && !this.$refs.scriptBuilder?.isArray, allowBack: () => !this.info.skippedSteps.script }, 
						{ id: 'audioGenerator', text: this.$tCaption('Audio generation'), visible: () => this.hasAiScriptGenerator() },
						{ id: 'results', text: this.$tCaption('Results'), }
					],
					maxPassed: 0
				},
				debugFuncs: [],
				fastChange: {
					visible: false,
					enabled: false,
					questions: []
				},
				states: {
					audioGenerated: false
				}
			};
		},
		computed: {
			filteredLanguages() {
				if (!this.brief?.AllowedLanguagesCodes) return this.languages;
				const codes = this.brief.AllowedLanguagesCodes.split(',').map(f => f.trim());
				return this.languages.filter(f => codes.includes(f.locale));
			},
			expertModeEnabled() {
				return this.brief?.ExpertMode && this.brief?.ExpertModeBrief?.Oid;
			},
			fixedMode() {
				return this.integrationInfo?.mode;
			},
			isDebug() {
				return this.$route.query?.debug == 'true';
			},
			info() {
				return {
					page: this.page,
					isLoading: this.isLoading,
					activatedPages: { start: true },
					apiKey: this.apiKey,
					briefId: this.briefId,
					brief: this.brief,
					mode: this.mode,
					branding: this.branding,
					brandingVars: this.brandingVars,
					questions: () => this.$refs.questions,
					templates: () => this.$refs.templates,
					scriptBuilder: () => this.$refs.scriptBuilder,
					voiceSelector: () => this.$refs.voiceSelector,
					adjectives: () => this.$refs.adjectives?.selectedAdjectives || [],
					language: () => this.getSelectedLanguage(),
					setLanguage: (locale) => this.setSelectedLanguage(locale),
					defaultLanguage: () => this.getDefaultLanguage(),
					audioGenerator: () => this.$refs.audioGenerator,
					musicSelection: () => this.$refs.musicSelection,
					producerRequest: () => this.$refs.producerRequest,
					results: () => this.$refs.results,
					answers: () => this.lastBriefAnswers,
					name: () => this.$refs.questions?.advertiserName ? this.$tCaption('<ADVERTISER> creative brief').replace(/<ADVERTISER>/g, this.$refs.questions?.advertiserName) : this.$tCaption('New creative brief'),
					lastAnswers: () => this.lastBriefAnswers,
					saveBriefAnswers: this.saveBriefAnswers,
					getBriefAnswersOnce: this.getBriefAnswersOnce,
					isDebug: () => this.isDebug,
					allowBack: () => this.activeStepIdx >= 0,
					integration: !this.integrationInfo ? null : {
						...this.integrationInfo,
						answers: this.integrationInfo?.answers && JSON.parse(this.integrationInfo?.answers)
					},
					generateCreativeName: this.generateCreativeName,
					allowedVoices() {
						if (this.branding?.AllowedVoices > 0) {
							return this.branding.AllowedVoices;
						}
						return this.brief.AllowedVoices;
					},
					predefined: this.predefined,
					defaultBranding: this.defaultBranding,
					skippedSteps: {
						script: this.brief?.HideScript,
						music: this.brief?.HideMusicSelection,
						template: this.brief?.SkipTemplateSelection
					},
					filteredLanguages: this.filteredLanguages,
					debugFuncs: this.debugFuncs,
					addDebugFunc(func) {
						if (!this.debugFuncs.includes(func)) {
							this.debugFuncs.push(func);
						}
					},
					removeDebugFunc(func) {
						if (this.debugFuncs.includes(func)) {
							this.debugFuncs.splice(this.debugFuncs.findIndex(v => v === func), 1);
						}
					},
					getDebugInfo: this.getDebugInfo,
					fastChange: this.fastChange,
					states: this.states,
					loadLastPassedBrief: this.loadLastPassedBrief
				}
			},
			companyLogoUrl() {
				return this.branding?.LogoUrl || this.brief?.LogoUrl;
			},
			isEditor() {
				return `${this.editor}` == 'true' && this.apiKey;
			},
			visibleStepperMainPages: {
				get: function() {
					return this.stepper.mainPages.filter(f => f.visible === void 0 || f.visible());
				},
				cache: false
			},
			displayingSteps: {
				get: function() {
					const questionsSteps = (!this.info.questions()?.isLoading && this.info.questions()?.orderedSteps?.map(f => f.Name) || []).map((f, subStepIdx) => ({ mainStepId: 'questions', subStepIdx, name: f }));
					return this.visibleStepperMainPages.reduce((res, mp) => [...res, ...(mp.id === 'questions' && questionsSteps.length > 1 ? questionsSteps : [{ mainStepId: mp.id, name: mp.text, allowBack: mp.allowBack }])], []);
				},
				cache: false
			},
			activeStepIdx: {
				get: function() {
					const questionsSteps = !this.info.questions()?.isLoading && this.info.questions()?.orderedSteps?.map(f => f.Name) || [];
					const cpIndex = this.visibleStepperMainPages.findIndex(v => v.id === this.page);
					const idx = this.page === 'questions' ? 
						(cpIndex + this.info.questions()?.activeStepIdx)
						: (cpIndex + ((this.visibleStepperMainPages.findIndex(v => v.id === 'questions') < cpIndex ? questionsSteps.length - 1: 0)));
					this.$nextTick(() => {
						this.stepper.maxPassed = Math.max(this.stepper.maxPassed, idx || 0);
					});
					return idx;
				},
				cache: false
			}
		},
		watch: {
			brandingStyleString() {
				this.applyStylings();
			},
			apiKey() {
				this.loadBranding();
			},
			briefId() {
				this.loadBrief();
				this.loadLastPassedBrief();
			},
			isDebug(v) {
				if (this.brief && v && !this.predefined) {
					this.loadLastPassedBrief();
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreative = this;

			this.$auth.getGlobalProcessId('brief_glob');
			
			this.loadBranding().finally(() => {
				this.loadBrief();

				if (!this.predefined) {
					this.loadLastPassedBrief();
				}
			});

			this.$unobserve = resizeObserver.observe(this, this.$refs.header, this.headerSizeChanged);
			this.headerSizeChanged();

			if (this.fixedMode === 'builder') {
				this.goToPage('questions');
			}

			if (this.fixedMode === 'templates') {
				this.goToPage('templates');
			}

			this.loadSpeechVoices();
		},
		destroyed() {
			document.body.classList.remove('easy-creative-app');
			delete this.$saveBriefAnswers;
		},
		methods: {
			showGenerateAnswers() {
				this.$refs.generateAnswers.show();
			},
			getDebugInfo() {
				let funcResults = this.info.debugFuncs.reduce((r, v) => {
					let res = v();
					return { ...r, ...(res || {}) };
				}, {});

				if (__DEBUG__)
					console.debug('showDebugInfo', { funcResults });

				return funcResults;
			},
			gotoExpertMode() {
				if (!this.expertModeEnabled) return;

				this.$router.push({ name: 'easy-creative-api-key', query: { briefId: this.brief.ExpertModeBrief?.Oid, sourceBriefId: this.briefId }, params: { apiKey: this.apiKey }});
			},
			gotoSimpleMode() {
				if (!this.sourceBriefId) return;

				this.$router.push({ name: 'easy-creative-api-key', query: { briefId: this.sourceBriefId }, params: { apiKey: this.apiKey }});
			},
			async generateCreativeName() {
				try {
					let script = this.info.scriptBuilder().script?.filter(f => f.type === 'text').map(f => f.value).join('. ');
					let gptResult = (await this.$Data.postExternalApi('OpenAiGpt3/Run', {
						promptCode: 'CREATIVENAMEGENERATOR',
						clientIp: this.$auth.userIP(),
						parameters: {
							SCRIPT: script
						}
					}));

					if (__DEBUG__)
						console.debug('generateCreativeName', { gptResult });

					return gptResult?.Data?.Content;
				} catch(ex) {
					this.$notifyError(ex);
				}
			},
			onDisplayStepClick(step) {
				if (__DEBUG__)
					console.debug('onDisplayStepClick', { step });
				this.page = step.mainStepId;
				if (step.subStepIdx !== void 0 && this.page === 'questions') {
					this.info.questions().activeStepIdx = step.subStepIdx;
				}
			},
			refreshScrollView(setTop) {
				setImmediate(() => {
					if (this.$refs.scrollView?.instance) {
						this.$refs.scrollView.instance.release();
						if (setTop) {
							this.$refs.scrollView.instance.scrollTo(0);
						}
					}
				});
			},
			headerSizeChanged() {
				this.headerOffsetHeight = this.$refs.header.offsetHeight;
			},
			async loadLastPassedBrief(opts) {
				this.lastBriefAnswers = null;

				let generateAI = opts?.generate && this.info.isDebug();

				if (this.$auth.isAnonym() && !generateAI) return;

				if (generateAI) {
					this.isLoading++;
					await this.$Data.postExternalApi('BriefAnswer/AiGenerate', { briefId: this.briefId, ...(opts?.data || {}) })
						.then(r => {
							this.lastBriefAnswers = r?.Data;
						})
						.finally(() => this.isLoading--);
					return;
				}

				this.isLoading++;
				let results = (await this.$Data.getExternalApi('BriefAnswer/List', 
					`sorting=[{ selector: 'Created', desc: true }]&filterCriteria=[` +
						`['Brief.Oid', '=','${this.briefId}'], 'and',` + 
						`['CreatedBy.Oid', '=','${this.$auth.userId()}'], 'and', ` + 
						`['IntegrationSetting.Oid', '=', ${this.integrationInfo ? `'${this.integrationInfo.integrationId}'` : 'null'}]]`, 1)
					.finally(() => this.isLoading--))?.Results;
				if (results?.length) {
					this.isLoading++;
					this.lastBriefAnswers = await this.$Data.getExternalApi('BriefAnswer/Get', `id=${results[0].Oid}`).finally(() => this.isLoading--);
				}
			},
			onMusicSelectionNext() {
				if (this.hasAiScriptGenerator())
					this.goToPage('audioGenerator', true);
				else 
					this.goToPage('results', true);
			},
			getMusicSelectionIsVisible() {
				return this.getMusicAdjectivesVisible() && (this.info.adjectives()?.length && (this.info.scriptBuilder()?.isFixedDuration || this.info.scriptBuilder()?.getScriptDuration()));
			},
			getMusicAdjectivesVisible() {
				return this.$refs.scriptBuilder?.music && !this.$refs.scriptBuilder?.music?.disabled && !this.$refs.scriptBuilder?.music?.adjectives?.length;
			},
			onMusicAdjectivesNext() {
				if (this.getMusicSelectionIsVisible()) {
					this.goToPage('musicSelection', true);
				} else {
					this.onMusicSelectionNext();
				}
			},
			applyStylings() {
				document.body.setAttribute('style', this.brandingStyleString);
			},
			async loadBranding() {
				if (this.predefined) {
					return;
				}

				document.body.classList.remove('easy-creative-app');
				if (!document.body.classList.contains('easy-creative-app')) {
					document.body.classList.add('easy-creative-app');
					this.applyStylings();
				}
				if (!this.apiKey?.length || this.apiKey?.length < 10) return;
				this.isLoading++;
				this.branding = await this.$Data.getExternalApi('BriefBranding/Get', `id=${this.apiKey}`).finally(() => this.isLoading--);
				if (this.branding) try {
					this.branding.JsonConfig = this.branding.JsonConfig && JSON.parse(this.branding?.JsonConfig) || {}; 
				} catch(ex) {
					this.branding.JsonConfig = {};
					if (__DEBUG__) {
						console.error('loadBranding', { ex });
					}
				}
				this.processBranding();
			},
			processBranding() {
				if (!this.branding) return;

				if (this.branding.PageTitle) {
					document.title = this.branding.PageTitle;
				}
			},
			scriptBuilderNext() {
				if (this.$refs.scriptBuilder.isArray) {
					if (!this.$refs.scriptBuilder.music || this.$refs.scriptBuilder.music?.disabled || this.$refs.scriptBuilder.music?.adjectives?.length) {
						this.goToPage('audioGenerator', true);
					} else {
						this.goToPage('adjectives', true);
					}
				} else {
					this.goToPage('voiceSelector', true);
				}
			},
			loadBrief() {
				if (this.predefined?.brief) {
					this.brief = this.predefined.brief;
					return;
				}
				if (this.briefId) {
					this.isLoading++;
					this.$Data.getExternalApi('Brief/GetBrief', `id=${this.briefId}`).then(r => {
						this.brief = r;
						if (r?.SkipTemplateSelection) {
							this.goToPage('questions');
						}
					}).finally(() => this.isLoading--);
					this.applyStylings();
				}
			},
			pageActivated(page) {
				return this.activatedPages && this.activatedPages[page] || false;
			},
			goToPage(page, isNext) {
				if (this.predefined) {
					if (!['questions', 'start'].includes(page)) {
						return;
					}
				}
				if (['questions', 'templates'].includes(page)) {
					this.mode = page;
				}
				this.$set(this.activatedPages, page, true);
				this.page = page;
				this.$nextTick(() => {
					if (isNext && this.$refs[page] && this.$refs[page].onActivate) {
						this.$refs[page].onActivate();
					}
					if (this.$refs[page].onShow) {
						this.$refs[page].onShow();
					}
					this.refreshScrollView(true);
				});
			},
			back() {
				let step = this.displayingSteps[this.activeStepIdx-1];
				let currentPos = 1;
				while(step?.allowBack && !step.allowBack()) {
					currentPos += 1;
					step = this.displayingSteps[this.activeStepIdx - currentPos];
				}
				if (step) {
					this.goToPage(step.mainStepId);
					if (step.subStepIdx) {
						this.info.questions().activeStepIdx = step.subStepIdx;
					}
				} else {
					this.goToPage('start');	
				}
				this.refreshScrollView(true);
			},
			hasAiScriptGenerator() {
				return this.$refs.questions?.brief?.AiScriptGenerator?.Oid;
			},
			getSelectedLanguage() {
				const question = (this.$refs.questions?.allQuestions || []).find(f => f?.__type === 'language' && f?.Answer);
				return question?.Answer?.locale ? question?.Answer : {
					locale: 'en-US',
					text: 'English (United States)'
				};
			},
			setSelectedLanguage(locale) {
				if (__DEBUG__)
					console.debug('setLanguage', { locale });

				if (!locale) return;
				const question = (this.$refs.questions?.allQuestions || []).find(f => f?.__type === 'language');
				if (question) {
					this.$set(question, 'Answer', {
						locale: locale?.locale,
						text: locale?.text
					});
				}
			},
			getDefaultLanguage() {
				const question = this.$refs.questions.allQuestions.find(f => f.__type === 'language');
				return question?.__defaultLanguage || {
					locale: 'en-US',
					text: 'English (United States)'
				};
			},
			getStringValue(val) {
				if (typeof(val) === 'string') return val;
				return JSON.stringify(val);
			},
			async saveBriefAnswers(creativeId) {
				const questions = this.info.questions();
				const scriptBuilder = this.info.scriptBuilder();
				
				this.$saveBriefAnswers = (this.$saveBriefAnswers || 0) + 1;

				if (this.$saveBriefAnswers > 1) return;
				
				const lastBriefAnswers = this.lastBriefAnswers && !this.lastBriefAnswers.CreativeId ? this.lastBriefAnswers : {
					Oid: null,
					Name: this.$tCaption('New brief answers'),
					Brief: {
						Oid: questions.brief?.Oid
					},
					AnswerItems: []
				}

				if (creativeId && !lastBriefAnswers.Creative?.Oid) {
					lastBriefAnswers.Creative = { Oid: creativeId };
				}

				lastBriefAnswers.Script = scriptBuilder?.scriptText;
				lastBriefAnswers.AnswerItems = questions.getFormedResults().reduce((res, s) => [...res, ...s.questions], []).map(q => ({
					Oid: lastBriefAnswers.AnswerItems.find(f => f.Question === q.Question)?.Oid,
					Value: this.getStringValue(q.AnswerValue),
					DisplayValue: this.getStringValue(q.Answer),
					Question: q.Question,
					BriefItemId: q.QuestionId,
					AiPrompt: q.AiPrompt
				}));

				if (this.integrationInfo) {
					lastBriefAnswers.IntegrationInfoJson = {
						UserEmail: this.integrationInfo.email,
						UserName: this.integrationInfo.userName,
						Answers: this.integrationInfo.answers
					}
					lastBriefAnswers.IntegrationSetting = {
						Oid: this.integrationInfo.integrationId
					}
				}
				
				this.$saveBriefAnswersPromise = this.$Data.postExternalApi('BriefAnswer/CreateUpdateBriefAnswer', lastBriefAnswers).then(r => {
					if (r?.Data) {
						this.lastBriefAnswers = r?.Data;
					}
				}).finally(() => {
					this.$saveBriefAnswers--;
					if (this.$saveBriefAnswers > 0) {
						this.$saveBriefAnswers = 0;
						return this.saveBriefAnswers();
					}
				});

				return this.$saveBriefAnswersPromise;
			},
			async getBriefAnswersOnce() {
				if (this.lastBriefAnswers) return this.lastBriefAnswers;
				if (this.$saveBriefAnswersPromise) await this.$saveBriefAnswersPromise;
				return await this.saveBriefAnswers();
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative {
		.editor-button {
			position: absolute;
			right: 5px;
			top: 5px;
		}

		.max-limited {
			max-width: 720px;
			width: 100%;
		}

		.hint {
			color: #7e7e7e;

			&:hover {
				color: $base-accent;
			}
		}

		.tile {
			text-align: center;
			width: 280px;
			height: 220px;
			background: #fff;
			margin: 4px;
			border-radius: 8px;
			border: 2px solid #fff;
			font-size: 18px;
			font-weight: 100;
			box-shadow: 2px 2px 13px 2px rgba(0,0,0,0.1);
			cursor: pointer;
			transform: scale(1);
			padding: 20px;
			user-select: none;

			.hint {
				font-size: 14px;
				color: #8b8b8b;
			}

			&:hover, &.active {
				transition: 0.3s;
				border: 2px solid $base-accent;
				transform: scale(1.025);
				color: $base-accent;

				.hint {
					color: $base-accent;
				}
			}

			&.selected {
				color: $base-accent;

				.hint {
					color: $base-accent;
				}
			}

			&.success {
				border: 2px solid $base-accent;
				background: $base-accent;
				color: $base-bg;
			}
		}

		.page {
			min-height: 600px;
			margin-top: 10px;
			padding: 10px;
		}

		.company-logo {
			max-width: 220px;
			max-height: 80px;
		}
		
		.powered-by {
			padding-left: 10px;
			border-left: 2px solid #dcdcdc;
			margin-left: 6px;

			.adthos-logo {
				max-width: 120px;
				max-height: 120px;
				margin-top: -10px;
				margin-left: -4px;
			}
		}

		.actions-divider {
			border-top: 2px solid #e6e6e6;
		}

		.line-loader {
			width: 100%;
			height: 16px;
			background: #dedede;
			position: relative;
			border-radius: 8px;
			overflow: hidden;
			transition: 0.3s;

			&:after {
				content: attr(text);
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translateX(-50%) translateY(-50%);
				font-size: 12px;
				transition: 0.3s;
			}

			&:before {
				content: '';
				position: absolute;
				left: 0px;
				top: 0px;
				height: 100%;
				background: $base-accent;
				width: var(--percentage);
				transition: 0.3s;
			}

			&.success {
				&:before {
					background: #2acc7a;
				}

				&:after {
					color: #fff;
				}
			}

		}

		.header {
			margin-top: 10px;
		}

		.easy-creative-main-scroll-view {
			position: fixed;
			left: 0px;
			top: var(--top);
			height: calc(100vh - var(--top));
			right: 0px;
		}

		.header-panel {			
			padding: 10px;
			--branding-bg: var(--branding-header-bg);
			--branding-color: var(--branding-header-color);
			--branding-input-bg: var(--branding-header-bg);
			--branding-input-color: var(--branding-header-color);
			color: var(--branding-header-color);
			background: radial-gradient(circle, var(--branding-header-bg-to) 0%, var(--branding-header-bg-from) 100%);
		}
		
		.stepper-line {
			height: 2px;
			width: 100%;
			position: relative;
			background: var(--branding-input-bg);
			border-radius: 2px;

			.stepper-value {
				border-radius: 2px;
				transition: 0.3s;
				position: absolute;
				left: 0px;
				top: 0px;
				height: 100%;
				width: var(--percent);
				background: var(--branding-highlight-color);
			}
		}

		.header-stepper {
			padding: 4px;
			background: var(--branding-input-bg);
			color: var(--branding-input-color);

			.step {
				opacity: 0.25;
				font-weight: 100;
				transition: 0.3s;
				user-select: none;

				.step-name {
					transition: 0.2s;
				}

				&.passed {
					opacity: 1;
					cursor: pointer;

					.step-name {
						&:hover {
							color: var(--branding-highlight-color);
						}
					}
				}

				&.active {
					.step-name {
						font-weight: 500;
					}
				}
			}
		}

		.top-right-panel {
			position: fixed;
			top: 20px;
			right: 30px;
		}
	}

	@import "./easy-creative-styles.scss";
</style>
